import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { TextField } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { ErrorModal } from "@dexteel/mesf-core";
import LazyLoading from "@/components/loaders/LazyLoading/LazyLoading";
import type { Drawing } from "../../../../models/TemplateDrawing";
import { useTemplatesContext } from "@/pages/templates/context/templatesContext";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
interface Props {
	templateDrawings: Drawing[];
	onSelectionChanged: (selectedDrawings: Drawing[]) => void;
}

export const AssetDrawingsTable = ({
	templateDrawings,
	onSelectionChanged,
}: Props) => {
	const [filterInput, setFilterInput] = useState("");
	const [error, setError] = useState<string>("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const {
		state: { assetDrawings },
	} = useTemplatesContext();

	const handlerFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFilterInput(e.target.value);
	};

	const sortRows = () => {
		gridApi?.applyColumnState({
			state: [{ colId: "DocumentName", sort: "asc", sortIndex: 0 }],
		});
	};

	const loadingOverlayComponent = useMemo(() => LazyLoading, []);
	const rows = useMemo(() => {
		const uniqueDrawings = new Map();
		assetDrawings.forEach((drawing: any) => {
			const key = drawing.DocumentName;
			if (!uniqueDrawings.has(key)) {
				uniqueDrawings.set(key, {
					...drawing,
					id: key,
					isSelected: templateDrawings.some(
						(wd) =>
							wd.DocumentName === drawing.DocumentName && wd.IsAssetResource,
					),
					assets: [
						{
							name: drawing.AssetName,
							id: drawing.AssetId,
							path: drawing.FullPath,
						},
					],
				});
			} else {
				const existingDrawing = uniqueDrawings.get(key);
				existingDrawing.assets.push({
					name: drawing.AssetName,
					id: drawing.AssetId,
					path: drawing.FullPath,
				});
			}
		});
		return Array.from(uniqueDrawings.values());
	}, [assetDrawings, templateDrawings]);

	const columns: ColDef[] = [
		{
			headerName: "",
			flex: 1,
			maxWidth: 100,
			headerCheckboxSelection: true,
			checkboxSelection: true,
			sortIndex: 1,
		},
		{
			field: "DocumentName",
			headerName: "Document Name",
			flex: 3,
			sortIndex: 1,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.DocumentName?.localeCompare(
						nodeB.data.DocumentName,
					);
				}
			},
		},
		{
			field: "Title",
			headerName: "Title",
			flex: 9,
			sortIndex: 0,
			comparator: (a: any, b: any, nodeA, nodeB, isInverted) => {
				if (nodeA.isSelected() && !nodeB.isSelected()) {
					return -1;
				} else if (!nodeA.isSelected() && nodeB.isSelected()) {
					return 1;
				} else {
					return nodeA.data.Title?.localeCompare(nodeB.data.Title);
				}
			},
		},
	];

	const onAgGridSelectionChanged = (event: SelectionChangedEvent) => {
		sortRows();
		const selectedRows = event.api.getSelectedRows();
		onSelectionChanged(selectedRows);
	};

	const onFirstDataRendered = useCallback(() => {
		gridApi?.forEachNode((node) => {
			if (node.data.isSelected) {
				node.setSelected(true);
			}
		});
		sortRows();
	}, [gridApi, templateDrawings]);

	useEffect(() => {
		gridApi?.setGridOption("quickFilterText", filterInput.trim());
	}, [filterInput, gridApi]);

	const onGridReady = (event: GridReadyEvent) => {
		setGridApi(event.api);
	};

	return (
		<>
			<Grid2
				container
				style={{ padding: "0 0 10px", justifyContent: "space-between" }}
			>
				<Grid2 size={{ xs: 12 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={filterInput}
						onChange={handlerFilter}
						margin="dense"
						fullWidth
					/>
				</Grid2>
			</Grid2>
			<div style={{ width: "100%", height: "45vh" }}>
				<AgGridReact
					getRowId={(params) => params.data.id}
					rowHeight={30}
					headerHeight={25}
					animateRows={true}
					onGridReady={onGridReady}
					rowData={rows}
					columnDefs={columns}
					defaultColDef={globalDefaultColDef}
					onFirstDataRendered={onFirstDataRendered}
					loadingOverlayComponent={loadingOverlayComponent}
					rowSelection="multiple"
					rowMultiSelectWithClick
					onSelectionChanged={onAgGridSelectionChanged}
					suppressContextMenu={true}
					loading={isLoading}
				/>
			</div>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
